import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ImageIcon } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { resolveAsset } from "../../context/CompanyContext";

export const CompanyField = ({ label, value, onChange, testId, placeholder = "", type = "text", hint, mono = false }) => (
  <label className="block space-y-2">
    <span className="text-[11px] uppercase tracking-widest text-[#a0b2af]">{label}</span>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 outline-none focus:border-[#d4af37] transition-colors duration-200 ${mono ? "font-mono tracking-wide" : ""}`}
      data-testid={testId}
    />
    {hint && <span className="block text-[11px] text-[#7d918d]">{hint}</span>}
  </label>
);

export const CompanySection = ({ eyebrow, title, children, testId }) => (
  <section className="lux-card p-6 md:p-8 space-y-6" data-testid={testId}>
    <div>
      <div className="eyebrow mb-2">{eyebrow}</div>
      <h2 className="font-display text-2xl">{title}</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
  </section>
);

export function LogoUploader({ variant, url, custom, onChange }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const dark = variant === "dark";

  async function upload(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/company/logo?variant=${variant}`, fd);
      onChange(data);
      toast.success("Logótipo atualizado.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  }

  async function remove() {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/company/logo?variant=${variant}`);
      onChange(data);
      toast.success("Logótipo reposto para o original.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-3" data-testid={`company-logo-${variant}`}>
      <div className="text-[11px] uppercase tracking-widest text-[#a0b2af]">
        {dark ? "Logótipo horizontal (cabeçalho do site/app)" : "Logótipo principal (e-mails e PDFs)"}
      </div>
      <div className={`h-36 rounded-sm border border-[#1a4640] grid place-items-center p-4 ${dark ? "bg-[#fdfbf7]" : "bg-white"}`}>
        {url ? (
          <img src={resolveAsset(url)} alt="Logótipo" className="max-h-28 max-w-full object-contain"
               data-testid={`company-logo-${variant}-preview`} />
        ) : <ImageIcon className="text-[#7d918d]" />}
      </div>
      <div className="flex gap-2 flex-wrap">
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden
               onChange={(e) => upload(e.target.files?.[0])} data-testid={`company-logo-${variant}-input`} />
        <button type="button" disabled={busy} onClick={() => input.current?.click()}
                className="btn-gold px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-60"
                data-testid={`company-logo-${variant}-upload-btn`}>
          <Upload size={14} /> {busy ? "A enviar…" : "Carregar"}
        </button>
        {custom && (
          <button type="button" disabled={busy} onClick={remove}
                  className="px-4 py-2 text-xs inline-flex items-center gap-2 border border-[#1a4640] rounded-sm hover:border-red-400 hover:text-red-300 transition-colors duration-200"
                  data-testid={`company-logo-${variant}-remove-btn`}>
            <Trash2 size={14} /> Repor original
          </button>
        )}
      </div>
    </div>
  );
}
